import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import Layout from './components/Layout';
import AuthGate from './components/AuthGate';
import AdminGuard from './components/AdminGuard';
import { PageSkeleton } from './components/Skeleton';
import './auth';   // 全局 fetch wrap 副作用

// U2: 路由级拆包。公开页、用户工作台、研究台与管理员后台不再进入同一个主 bundle。
const Dashboard = lazy(() => import('./pages/Dashboard'));
const ExecutionRisk = lazy(() => import('./pages/ExecutionRisk'));
const EvidenceAlpha = lazy(() => import('./pages/EvidenceAlpha'));
const Strategies = lazy(() => import('./pages/Strategies'));
const Candidates = lazy(() => import('./pages/Candidates'));
const Trades = lazy(() => import('./pages/Trades'));
const Audit = lazy(() => import('./pages/Audit'));
const Settings = lazy(() => import('./pages/Settings'));
const MyAlphas = lazy(() => import('./pages/MyAlphas'));
const Pricing = lazy(() => import('./pages/Pricing'));
const Checkout = lazy(() => import('./pages/Checkout'));
const Terms = lazy(() => import('./pages/Terms'));
const RefundPolicy = lazy(() => import('./pages/RefundPolicy'));
const Privacy = lazy(() => import('./pages/Privacy'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const Login = lazy(() => import('./pages/Login'));
const MarketingNav = lazy(() => import('./components/MarketingNav'));
const UpgradeModal = lazy(() => import('./components/UpgradeModal'));
const AdminUsers = lazy(() => import('./pages/AdminUsers'));
const AdminUserDetail = lazy(() => import('./pages/AdminUserDetail'));
const AdminRevenue = lazy(() => import('./pages/AdminRevenue'));
const AdminAuditLog = lazy(() => import('./pages/AdminAuditLog'));
const AdminQuantcore = lazy(() => import('./pages/AdminQuantcore'));

const globalStyle = document.createElement('style');
globalStyle.textContent = `
  :root {
    /* Phase 12.15.6: 金融科技混合 — 取代 cyberpunk neon multi-color */
    --bg-void: #0b0c10;
    --bg-deep: #07080b;
    --bg-mid: #12141b;
    --bg-surface: #12141b;
    --bg-surface-elevated: #1b1e27;
    --bg-glass: rgba(18, 20, 27, 0.6);
    --border: rgba(148, 163, 184, 0.12);
    --border-hot: rgba(148, 163, 184, 0.24);
    --primary: #a78bfa;
    --primary-glow: rgba(167, 139, 250, 0.5);
    --accent: #a78bfa;
    --accent-glow: rgba(167, 139, 250, 0.4);
    /* Phase 12.25: 删 neon-pink (unused) — 留 neon-purple alias 给 brand 紫 */
    --neon-purple: #a78bfa;
    --gold: #f7a600;
    --gold-deep: #d68900;
    --warn-yellow: #f7a600;
    --success: #00d4aa;
    --error: #ff4757;
    --error-bright: #ff4757;
    --warning: #f7a600;
    --text: #e2e8f0;
    --text-dim: #94a3b8;
    --text-faint: #64748b;
  }

  html, body, #root {
    background: var(--bg-void);
    color: var(--text);
    margin: 0;
    min-height: 100vh;
    overflow-x: hidden;
  }

  body {
    /* 2026-06-11 金融×赛博: 双色氛围光(紫=AI右上 / cyan=数据左下) + 超细HUD网格
       全部静态背景 — 一次paint零持续GPU开销 */
    background:
      radial-gradient(ellipse 50% 30% at 85% -5%, rgba(167,139,250,0.07), transparent 70%),
      radial-gradient(ellipse 40% 25% at -5% 105%, rgba(124,58,237,0.05), transparent 70%),
      repeating-linear-gradient(0deg, rgba(148,163,184,0.022) 0 1px, transparent 1px 44px),
      repeating-linear-gradient(90deg, rgba(148,163,184,0.022) 0 1px, transparent 1px 44px),
      linear-gradient(180deg, #0b0c10 0%, #07080b 100%);
    background-attachment: fixed;
    font-feature-settings: 'tnum' 1, 'cv11' 1;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    position: relative;
  }

  /* === 網格已并入 body background(静态) === */
  body::before {
    display: none;
  }

  /* === 赛博细节: 滚动条/选区/焦点 — 全站统一 === */
  ::-webkit-scrollbar { width: 8px; height: 8px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb {
    background: rgba(167,139,250,0.22);
    border-radius: 4px;
    border: 2px solid transparent;
    background-clip: content-box;
  }
  ::-webkit-scrollbar-thumb:hover { background-color: rgba(167,139,250,0.45); }
  ::selection { background: rgba(167,139,250,0.35); color: #f8fafc; }

  /* HUD 状态点: 单个2px小点的呼吸光 — 唯一允许的常驻动画(开销≈0) */
  @keyframes hud-pulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(0,212,170,0.5); }
    50% { box-shadow: 0 0 6px 2px rgba(0,212,170,0.25); }
  }
  .hud-dot {
    display: inline-block; width: 7px; height: 7px; border-radius: 50%;
    background: #00d4aa; animation: hud-pulse 2.4s ease-in-out infinite;
  }
  @media (prefers-reduced-motion: reduce) {
    .hud-dot { animation: none; }
    .global-scanline { animation: none; opacity: 0; }
  }

  /* CRT 條紋已移除 - GPU 開銷大 */

  /* === Scan line：只在進站時掃一次，避免持續 GPU 渲染 === */
  @keyframes scanline-once {
    0% { transform: translate3d(0, 0, 0); opacity: 0; }
    10% { opacity: 1; }
    90% { opacity: 1; }
    100% { transform: translate3d(0, 100vh, 0); opacity: 0; }
  }
  .global-scanline {
    position: fixed;
    left: 0; right: 0; top: 0;
    height: 80px;
    background: linear-gradient(
      to bottom,
      transparent 0%,
      rgba(167, 139, 250, 0.08) 50%,
      transparent 100%
    );
    pointer-events: none;
    z-index: 9997;
    animation: scanline-once 2.5s ease-out 1;
    will-change: transform, opacity;
  }

  /* === 數字 mono === */
  .num-mono {
    font-family: 'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, monospace;
    font-feature-settings: 'tnum' 1, 'zero' 1;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.02em;
  }

  /* === 2026-06-11 赛博化: 渐变面+受光顶边, 删 blur(8px)(全站GPU负担) === */
  .glass-card {
    background: linear-gradient(180deg, rgba(30,33,42,0.92) 0%, rgba(15,16,21,0.96) 100%);
    border: 1px solid rgba(167, 139, 250, 0.16);
    border-radius: 8px;
    position: relative;
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.04);
    transition: border-color 200ms, box-shadow 200ms;
  }
  .glass-card:hover {
    border-color: rgba(167, 139, 250, 0.34);
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.04), 0 0 18px rgba(167,139,250,0.08);
  }

  /* === 深層 panel（卡中卡）=== */
  .glass-inner {
    background: rgba(8, 9, 12, 0.6);
    border: 1px solid rgba(148, 163, 184, 0.08);
    border-radius: 6px;
  }

  /* === 数据行 — table-like 紧凑 row === */
  .data-row {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    border-bottom: 1px solid rgba(148, 163, 184, 0.06);
    font-family: "JetBrains Mono", monospace;
    font-size: 12px;
    transition: background-color 120ms;
  }
  .data-row:hover {
    background-color: rgba(167, 139, 250, 0.04);
  }
  .data-row:last-child {
    border-bottom: none;
  }

  /* === Section label — 给 panel 内部分组用 === */
  .section-label {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #64748b;
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
  }
  .section-label::before {
    content: '';
    width: 2px;
    height: 12px;
    background: #a78bfa;
    box-shadow: 0 0 6px rgba(167,139,250,0.6);
  }

  /* === 脈衝點 === */
  @keyframes pulse-dot {
    0%, 100% { transform: scale(1); opacity: 1; box-shadow: 0 0 8px currentColor; }
    50% { transform: scale(1.5); opacity: 0.6; box-shadow: 0 0 16px currentColor; }
  }
  .pulse-dot { animation: pulse-dot 1.8s ease-in-out infinite; }

  /* === 雷達脈衝環（單環版，輕量）=== */
  @keyframes radar-pulse {
    0% { transform: scale3d(0.5, 0.5, 1); opacity: 0.8; }
    100% { transform: scale3d(2.5, 2.5, 1); opacity: 0; }
  }
  .radar-pulse-container {
    position: relative;
    width: 12px; height: 12px;
    display: inline-block;
  }
  .radar-pulse-dot {
    position: absolute;
    width: 8px; height: 8px;
    top: 2px; left: 2px;
    border-radius: 50%;
    background: var(--success);
    box-shadow: 0 0 6px var(--success);
    z-index: 2;
  }
  .radar-pulse-ring {
    /* 動畫已關閉以減少持續 repaint；保留靜態樣式 */
    animation: none !important;
    position: absolute;
    width: 12px; height: 12px;
    top: 0; left: 0;
    border-radius: 50%;
    border: 2px solid var(--success);
    animation: radar-pulse 2s ease-out infinite;
    will-change: transform, opacity;
  }

  /* === 流光邊框 === */
  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
  .glow-border {
    position: relative;
  }
  .glow-border::after {
    /* Phase 5.5+ — 移除了 shimmer 動畫（持續 repaint 影響整站效能）。
       保留 class 兼容既有 markup，但只給靜態微邊框。 */
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    padding: 1px;
    border: 1px solid rgba(99, 102, 241, 0.25);
    pointer-events: none;
  }

  /* Glitch 已移除 — filter:hue-rotate 開銷大 */

  /* === 終端打字效果（caret 閃爍）=== */
  @keyframes caret-blink {
    0%, 50% { opacity: 1; }
    51%, 100% { opacity: 0; }
  }
  .caret {
    display: inline-block;
    width: 8px;
    height: 1em;
    background: var(--accent);
    margin-left: 4px;
    vertical-align: text-bottom;
    animation: caret-blink 1s steps(1) infinite;
    box-shadow: 0 0 8px var(--accent);
  }

  /* === 警告斜紋（黃黑）=== */
  .warning-stripes {
    background-image: repeating-linear-gradient(
      -45deg,
      rgba(250, 204, 21, 0.18),
      rgba(250, 204, 21, 0.18) 8px,
      rgba(0, 0, 0, 0.35) 8px,
      rgba(0, 0, 0, 0.35) 16px
    );
  }

  /* === 文字光暈 === */
  /* 文字光暈降低 — 從雙層 24/48px 收成單層 12px 0.4 (效能 + 清晰兼顧) */
  .glow-text-primary { text-shadow: 0 0 12px rgba(99, 102, 241, 0.4); }
  .glow-text-accent  { text-shadow: 0 0 12px rgba(167, 139, 250, 0.4); }
  .glow-text-success { text-shadow: 0 0 12px rgba(34, 197, 94, 0.4); }
  .glow-text-error   { text-shadow: 0 0 12px rgba(239, 68, 68, 0.4); }
  .glow-text-gold    { text-shadow: 0 0 12px rgba(251, 191, 36, 0.35); }

  /* === Ticker 跑馬燈（GPU 加速）=== */
  @keyframes ticker-scroll {
    0% { transform: translate3d(0, 0, 0); }
    100% { transform: translate3d(-50%, 0, 0); }
  }
  .ticker-content {
    display: inline-block;
    white-space: nowrap;
    /* ticker-scroll 動畫已關閉 — 持續 transform 重繪影響整站效能 */
    will-change: auto;
  }

  /* === 數據更新閃爍 === */
  @keyframes data-flash {
    0% { background-color: transparent; }
    20% { background-color: rgba(99, 102, 241, 0.25); }
    100% { background-color: transparent; }
  }
  .data-flash { animation: data-flash 800ms ease; }

  /* === Sparkline 小圖容器 === */
  .spark-cell {
    display: inline-block;
    vertical-align: middle;
  }

  /* === Recharts tooltip 玻璃版 === */
  .recharts-default-tooltip {
    background: rgba(8, 10, 24, 0.92) !important;
    backdrop-filter: blur(16px) saturate(160%);
    border: 1px solid rgba(99, 102, 241, 0.4) !important;
    border-radius: 8px !important;
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.6), 0 0 40px rgba(99, 102, 241, 0.2) !important;
    padding: 12px 16px !important;
  }
  .recharts-tooltip-label {
    color: #94a3b8 !important;
    font-size: 10px !important;
    font-weight: 600 !important;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    margin-bottom: 6px !important;
  }
  .recharts-tooltip-item {
    color: #e2e8f0 !important;
    font-size: 13px !important;
    font-family: 'JetBrains Mono', monospace;
  }
  .recharts-tooltip-item-value { font-weight: 600 !important; }

  /* === Scrollbar === */
  ::-webkit-scrollbar { width: 8px; height: 8px; }
  ::-webkit-scrollbar-track { background: rgba(99, 102, 241, 0.04); }
  ::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, rgba(99, 102, 241, 0.4), rgba(167, 139, 250, 0.4));
    border-radius: 4px;
  }
  ::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(180deg, rgba(99, 102, 241, 0.7), rgba(167, 139, 250, 0.7));
  }

  ::selection { background: rgba(99, 102, 241, 0.45); color: #fff; }

  /* === Reduced motion 對殘障 / 低端裝置友善 === */
  @media (prefers-reduced-motion: reduce) {
    .pulse-dot, .radar-pulse-ring, .ticker-content,
    .glow-border::after, .global-scanline, .caret {
      animation: none !important;
    }
  }

  /* === 低端裝置 fallback：縮小 blur === */
  @media (max-width: 768px) {
    .glass-card, .MuiCard-root, .MuiPaper-root {
      backdrop-filter: blur(8px) !important;
      -webkit-backdrop-filter: blur(8px) !important;
    }
  }
`;
document.head.appendChild(globalStyle);

// Phase 12.15.7: 全局重寫 MUI base theme → 金融科技混合 (Robinhood/Bybit)
// 之前所有 panel 內的 indigo / 紫色 / cyberpunk 渐变都來自這裡，一次換完全部跟著走
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary:   { main: '#a78bfa', light: '#c4b5fd', dark: '#7c3aed' },    // cyan (system chrome)
    secondary: { main: '#f7a600', light: '#fbbf24' },                       // Bybit 金黃 (高亮)
    success:   { main: '#00d4aa', light: '#34d399' },                       // Robinhood teal-green
    error:     { main: '#ff4757', light: '#fb7185' },                       // 玫瑰红
    warning:   { main: '#f7a600', light: '#fbbf24' },
    info:      { main: '#a78bfa' },
    background: {
      default: '#0b0c10',     // 主背景 dark navy
      paper:   '#12141b',     // panel 表面 — 实色，不再 backdrop-blur cyberpunk 玻璃
    },
    divider: 'rgba(148, 163, 184, 0.12)',
    text: {
      primary:   '#e2e8f0',
      secondary: '#94a3b8',
      disabled:  '#64748b',
    },
  },
  typography: {
    fontFamily: '"Inter", "Noto Sans TC", -apple-system, "Segoe UI", Roboto, sans-serif',
    h4: { fontWeight: 700, letterSpacing: '-0.02em' },
    h5: { fontWeight: 700, letterSpacing: '-0.01em' },
    h6: { fontWeight: 600, letterSpacing: '-0.005em' },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600 },
    body1: { letterSpacing: 0 },
    body2: { letterSpacing: 0, fontSize: '0.82rem' },
    button: { letterSpacing: 0.3, textTransform: 'none', fontWeight: 600 },
    caption: { letterSpacing: 0.3, fontWeight: 500, fontSize: '0.72rem' },
    overline: { letterSpacing: 1.2, fontWeight: 700, fontSize: '0.65rem' },
  },
  shape: { borderRadius: 8 },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          // 2026-06-11 赛博化: 实色渐变面(顶部受光) — 取代纯平实色
          background: 'linear-gradient(180deg, #15171f 0%, #0f1116 100%)',
          border: '1px solid rgba(167, 139, 250, 0.14)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          // 2026-06-11: 删每卡 backdrop-filter blur(8px)(全站GPU负担) → 渐变面+发光描边, 更快更有质感
          background: 'linear-gradient(180deg, rgba(30,33,42,0.92) 0%, rgba(15,16,21,0.96) 100%)',
          border: '1px solid rgba(167, 139, 250, 0.16)',
          borderRadius: 8,
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)',
          transition: 'border-color 180ms ease, box-shadow 180ms ease',
          '&:hover': {
            borderColor: 'rgba(167, 139, 250, 0.34)',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04), 0 0 18px rgba(167,139,250,0.10)',
          },
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { fontWeight: 600, textTransform: 'none', borderRadius: 6 },
        containedPrimary: {
          background: 'linear-gradient(180deg, #b79dfc 0%, #a78bfa 55%, #9272f5 100%)',
          color: '#0b0c10',
          boxShadow: '0 0 0 1px rgba(167,139,250,0.35), inset 0 1px 0 rgba(255,255,255,0.25)',
          '&:hover': {
            background: 'linear-gradient(180deg, #a78bfa 0%, #8b5cf6 100%)',
            boxShadow: '0 0 18px rgba(167, 139, 250, 0.45), inset 0 1px 0 rgba(255,255,255,0.2)',
          },
        },
        containedSecondary: {
          background: 'linear-gradient(180deg, #ffb52e 0%, #f7a600 100%)',
          color: '#0b0c10',
          '&:hover': { background: '#d68900', boxShadow: '0 0 14px rgba(247,166,0,0.4)' },
        },
        outlined: {
          borderColor: 'rgba(167,139,250,0.35)',
          '&:hover': { borderColor: '#a78bfa', backgroundColor: 'rgba(167,139,250,0.06)', boxShadow: '0 0 12px rgba(167,139,250,0.15)' },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, fontSize: 11, letterSpacing: 0.2, borderRadius: 5 },
        outlined: { borderColor: 'rgba(167, 139, 250, 0.28)' },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(148, 163, 184, 0.07)',
          fontFamily: '"Inter", "Noto Sans TC", sans-serif',
          fontSize: '0.78rem',
          padding: '8px 12px',
          fontVariantNumeric: 'tabular-nums',   // 金融铁律: 数字列对齐不跳动
        },
        head: {
          color: '#7c8db5',
          fontWeight: 700,
          fontSize: '0.65rem',
          textTransform: 'uppercase',
          letterSpacing: 1,
          background: 'linear-gradient(180deg, #0d0f14 0%, #0b0c10 100%)',
          padding: '10px 12px',
          borderBottom: '1px solid rgba(167, 139, 250, 0.22)',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color 120ms ease',
          '&:hover': { backgroundColor: 'rgba(167, 139, 250, 0.045)' },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          background: 'linear-gradient(180deg, #171a23 0%, #0f1116 100%)',
          border: '1px solid rgba(167,139,250,0.3)',
          color: '#e2e8f0',
          fontSize: 11.5,
          boxShadow: '0 4px 20px rgba(0,0,0,0.5), 0 0 10px rgba(167,139,250,0.08)',
        },
        arrow: { color: '#15171f', '&::before': { border: '1px solid rgba(167,139,250,0.3)' } },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          background: 'linear-gradient(180deg, #161821 0%, #0d0f15 100%)',
          border: '1px solid rgba(167,139,250,0.28)',
          boxShadow: '0 24px 64px rgba(0,0,0,0.65), 0 0 40px rgba(167,139,250,0.07)',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 3, backgroundColor: 'rgba(148,163,184,0.12)' },
        bar: { borderRadius: 3 },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: '#a78bfa',
          height: 2,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none', fontWeight: 600, fontSize: 13,
          color: '#94a3b8',
          '&.Mui-selected': { color: '#a78bfa' },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#0b0c10',
          borderBottom: '1px solid rgba(148, 163, 184, 0.12)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
          backgroundColor: '#07080b',
          borderRight: '1px solid rgba(148, 163, 184, 0.12)',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { backgroundColor: 'rgba(148, 163, 184, 0.08)' },
        bar: { backgroundColor: '#a78bfa' },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: 'rgba(148, 163, 184, 0.12)' },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: '#12141b',
          '& fieldset': { borderColor: 'rgba(148, 163, 184, 0.16)' },
          '&:hover fieldset': { borderColor: 'rgba(148, 163, 184, 0.32) !important' },
          '&.Mui-focused fieldset': { borderColor: '#a78bfa !important' },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 6 },
        standardSuccess: { backgroundColor: 'rgba(0,212,170,0.1)', color: '#34d399', border: '1px solid rgba(0,212,170,0.3)' },
        standardError: { backgroundColor: 'rgba(255,71,87,0.1)', color: '#fb7185', border: '1px solid rgba(255,71,87,0.3)' },
        standardWarning: { backgroundColor: 'rgba(247,166,0,0.1)', color: '#fbbf24', border: '1px solid rgba(247,166,0,0.3)' },
        standardInfo: { backgroundColor: 'rgba(167,139,250,0.1)', color: '#c4b5fd', border: '1px solid rgba(167,139,250,0.3)' },
      },
    },
  },
});

export default function App() {
  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      {/* Phase 12.20: 删 global-scanline 顶部扫描线（cyber 装饰） */}
      <BrowserRouter>
        <Suspense fallback={<PageSkeleton />}>
          {/* Phase 12.24.5: 全局 upgrade modal — 监听 fetch 402 自动弹出 */}
          <UpgradeModal />
          <Routes>
          {/* Phase 12.26: Marketing 公开路由（无需登录，无 sidebar）*/}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/pricing" element={<><MarketingNav /><Pricing /></>} />
          <Route path="/checkout" element={<><MarketingNav /><Checkout /></>} />
          <Route path="/terms" element={<><MarketingNav /><Terms /></>} />
          <Route path="/refund-policy" element={<><MarketingNav /><RefundPolicy /></>} />
          <Route path="/privacy" element={<><MarketingNav /><Privacy /></>} />

          {/* Phase 12.26: 受保护 app 路由（AuthGate + sidebar Layout）*/}
          <Route path="/" element={<AuthGate><Layout /></AuthGate>}>
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="execution" element={<ExecutionRisk />} />
            <Route path="evidence" element={<EvidenceAlpha />} />
            <Route path="my-alphas" element={<MyAlphas />} />
            <Route path="trades" element={<Trades />} />
            <Route path="settings" element={<Settings />} />
            {/* Strategy/Candidate 属于研究控制面，不再暴露为普通用户主任务。 */}
            <Route path="strategies" element={<AdminGuard><Strategies /></AdminGuard>} />
            <Route path="candidates" element={<AdminGuard><Candidates /></AdminGuard>} />
            <Route path="audit" element={<AdminGuard><Audit /></AdminGuard>} />
            {/* Phase 14j: Admin-only routes (双层守护: AdminGuard + backend require_admin) */}
            <Route path="admin/users" element={<AdminGuard><AdminUsers /></AdminGuard>} />
            <Route path="admin/users/:id" element={<AdminGuard><AdminUserDetail /></AdminGuard>} />
            <Route path="admin/revenue" element={<AdminGuard><AdminRevenue /></AdminGuard>} />
            <Route path="admin/audit" element={<AdminGuard><AdminAuditLog /></AdminGuard>} />
            <Route path="admin/quantcore" element={<AdminGuard><AdminQuantcore /></AdminGuard>} />
          </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </ThemeProvider>
  );
}
